import React from 'react';
import { FiUsers, FiTrendingUp, FiCheckCircle, FiClock, FiShield, FiZap, FiBarChart2, FiMail,  FiGlobe,  FiPrinter } from 'react-icons/fi';

const PaperlessAdmissions = () => {
  const steps = [
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: 'Step 1: Go Online',
      description: 'Move everything online. Application forms, document submission, fee payment, and communication. No more paper forms, no more manual data entry, no more physical document handling. Everything is digital from the start.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiZap className="w-6 h-6" />,
      title: 'Step 2: Simplify the Process',
      description: 'Remove unnecessary steps. Ask only for what you need. Use smart forms that adapt to each applicant. Make it possible to complete an application in under 10 minutes on any device.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiMail className="w-6 h-6" />,
      title: 'Step 3: Automate Communication',
      description: 'Set up automated emails and SMS for every stage. Confirmation immediately after submission. Document verification updates. Interview or assessment schedules. Admission offers. No manual follow-ups needed.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Step 4: Automate Verification',
      description: 'Use the system to verify documents automatically. Check formats, sizes, and completeness. Flag issues instantly so applicants can correct them immediately. No back-and-forth manual verification.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Step 5: Digital Decision Making',
      description: 'Streamline the evaluation process. Use digital workflows to route applications to the right reviewers. Track decisions in real-time. Make offers digitally. No lost papers, no delays.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Step 6: Track and Optimize',
      description: 'Use analytics to track your progress. See how many applications you\'re receiving, how quickly they\'re being processed, where bottlenecks exist, and how you can improve.',
      color: 'bg-blue-50'
    }
  ];

  const results = [
    {
      title: '10-Day Enrollment Target',
      description: 'Process 500+ applications and make admission offers within 10 working days.',
      icon: <FiClock className="w-5 h-5" />
    },
    {
      title: 'Zero Paper',
      description: 'Eliminate all paper forms, documents, and manual processes from admissions.',
      icon: <FiPrinter className="w-5 h-5" />
    },
    {
      title: '100% Digital Communication',
      description: 'All communication happens through automated emails and SMS. No manual follow-ups.',
      icon: <FiMail className="w-5 h-5" />
    },
    {
      title: 'Real-Time Visibility',
      description: 'Track every application, every decision, and every communication in real-time.',
      icon: <FiBarChart2 className="w-5 h-5" />
    },
    {
      title: 'Reduced Processing Time',
      description: 'Reduce admission processing time from weeks to days.',
      icon: <FiZap className="w-5 h-5" />
    },
    {
      title: 'Higher Conversion Rate',
      description: 'Reach more applicants, process them faster, and convert more into enrolled students.',
      icon: <FiTrendingUp className="w-5 h-5" />
    }
  ];

  const benefits = [
    {
      title: 'Faster Processing',
      description: 'Applications are processed in minutes, not days. Documents are verified instantly. Offers are made quickly.'
    },
    {
      title: 'Reduced Errors',
      description: 'Eliminate manual data entry errors. Automated validation ensures data accuracy.'
    },
    {
      title: 'Better Applicant Experience',
      description: 'Applicants appreciate the speed and transparency. They stay informed throughout the process.'
    },
    {
      title: 'Lower Administrative Cost',
      description: 'Reduce staff time spent on admissions. Free up resources for other priorities.'
    },
    {
      title: 'Increased Capacity',
      description: 'Process more applications with the same staff. Scale your enrollment without scaling your admission team.'
    },
    {
      title: 'Competitive Advantage',
      description: 'Institutions with paperless admissions attract more applicants and fill seats faster.'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Imagine enrolling 500 students in just 10 days. No paper forms. No manual data entry. No document piles. No lost applications. No frustrated parents waiting for updates. Just a smooth, fast, digital process that works.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        This isn't a dream. <strong className="text-slate-900">Institutions across India are already doing it—enrolling hundreds of students in days, not weeks.</strong> They've gone paperless, automated their admission processes, and transformed what used to be a stressful season into a smooth operation.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here's how you can do it too. A step-by-step guide to paperless admissions that will help you enroll 500+ students in 10 days.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">500+</div>
          <div className="text-sm text-slate-500">Students enrolled in 10 days</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">75%</div>
          <div className="text-sm text-slate-500">Reduction in processing time</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">100%</div>
          <div className="text-sm text-slate-500">Paperless admission process</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">The Paperless Admission Framework</h2>

      {/* Steps List */}
      {steps.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm">{item.description}</p>
            </div>
          </div>
        </div>
      ))}

      {/* Results Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">What You Can Achieve with Paperless Admissions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {results.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-3">
                <div className="bg-white rounded-lg p-2 flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Benefits Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Why Paperless Admissions Work</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {benefits.map((item, i) => (
            <div key={i} className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
              <div className="flex items-start gap-2">
                <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Paper-Based vs. Paperless Admissions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Paper-Based Admissions</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Weeks or months to process</span></li>
              <li className="flex items-start gap-2">• <span>Manual data entry and errors</span></li>
              <li className="flex items-start gap-2">• <span>Documents get lost</span></li>
              <li className="flex items-start gap-2">• <span>Limited applicant reach</span></li>
              <li className="flex items-start gap-2">• <span>Staff overwhelmed</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Paperless Admissions with Truyon</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>10 days to process 500+ applications</span></li>
              <li className="flex items-start gap-2">• <span>Automated data entry and validation</span></li>
              <li className="flex items-start gap-2">• <span>Digital documents, never lost</span></li>
              <li className="flex items-start gap-2">• <span>National and international reach</span></li>
              <li className="flex items-start gap-2">• <span>Staff focus on value-added work</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon Makes Paperless Admissions Possible</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📝</div>
            <h4 className="font-bold text-slate-900 text-sm">Online Application Forms</h4>
            <p className="text-xs text-slate-600">Smart forms that take 10 minutes or less</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📄</div>
            <h4 className="font-bold text-slate-900 text-sm">Digital Document Management</h4>
            <p className="text-xs text-slate-600">Upload, verify, and store documents digitally</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">💳</div>
            <h4 className="font-bold text-slate-900 text-sm">Online Payment</h4>
            <p className="text-xs text-slate-600">Integrated payment processing</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">💬</div>
            <h4 className="font-bold text-slate-900 text-sm">Automated Communication</h4>
            <p className="text-xs text-slate-600">Emails and SMS at every stage</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Fast Decisions</h4>
            <p className="text-xs text-slate-600">Automated workflows for quick processing</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Analytics Dashboard</h4>
            <p className="text-xs text-slate-600">Track progress and optimize</p>
          </div>
        </div>
      </div>

      {/* The Day-by-Day Plan */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">The 10-Day Plan</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-indigo-50 rounded-xl p-3 border border-indigo-200 text-center">
            <div className="text-xs font-bold text-indigo-600">Days 1-2</div>
            <p className="text-xs text-slate-600">Launch online application portal</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-200 text-center">
            <div className="text-xs font-bold text-emerald-600">Days 3-5</div>
            <p className="text-xs text-slate-600">Receive and process applications</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-3 border border-amber-200 text-center">
            <div className="text-xs font-bold text-amber-600">Days 6-7</div>
            <p className="text-xs text-slate-600">Verify documents and evaluate</p>
          </div>
          <div className="bg-rose-50 rounded-xl p-3 border border-rose-200 text-center">
            <div className="text-xs font-bold text-rose-600">Days 8-9</div>
            <p className="text-xs text-slate-600">Make admission offers</p>
          </div>
          <div className="bg-purple-50 rounded-xl p-3 border border-purple-200 text-center sm:col-span-2">
            <div className="text-xs font-bold text-purple-600">Day 10</div>
            <p className="text-xs text-slate-600">Complete enrollment and celebrate</p>
          </div>
        </div>
      </div>

      {/* The Transformational Impact */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">The Transformational Impact of Paperless Admissions</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          Paperless admissions don't just save paper. They transform your entire admission process. Staff stress decreases significantly when manual work is automated. Parents and students enjoy a smooth, transparent experience. Your institution builds a reputation for efficiency and innovation.
        </p>
        <p className="text-amber-800 leading-relaxed mb-3">
          Most importantly, you can enroll more students faster. In a competitive education market, speed and experience matter. Institutions with paperless admissions attract more applicants, convert them faster, and fill their seats quicker than those still using paper.
        </p>
        <p className="text-amber-800 leading-relaxed">
          <strong>Paperless isn't just about technology—it's about transforming how you serve students and families.</strong>
        </p>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Enroll 500+ Students in 10 Days with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          The admission season doesn't have to be stressful. With a paperless process, you can enroll hundreds of students in days, not weeks. Your staff works efficiently. Your applicants have a smooth experience. Your institution builds a stronger reputation.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon makes paperless admissions possible. Online applications, digital document management, automated communication, fast decision-making—everything you need to enroll 500+ students in 10 days.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for paperless admissions.</strong> Enroll faster. Serve better. Build stronger.
        </p>
      </div>
    </>
  );
};

export default PaperlessAdmissions;